import NextHead from 'next/head';

import { getJobsUrl } from '~/config';
import { useBusinessUnitContext } from '~/contexts/BusinessUnitContext/BusinessUnitContext';
import type { HreflangLink } from '~/utils/hreflang';

export const NOINDEX_FOLLOW = 'noindex, follow';

type HeadProps = {
  title: string;
  description?: string;
  socialTitle?: string;
  socialDescription?: string;
  hreflangLinks?: HreflangLink[];
  paginationLinks?: { prev?: string; next?: string };
  robots?: string;
};

export const Head = ({
  title,
  description,
  socialTitle,
  socialDescription,
  hreflangLinks,
  paginationLinks,
  robots = 'index, follow',
}: HeadProps) => {
  const { metaImage } = useBusinessUnitContext();
  // a link shared on a social network shows the page's own title rather than the site's name alone
  const ogTitle = socialTitle || title;
  const ogDescription = socialDescription || description;
  const ogImage = `${getJobsUrl()}/${metaImage}`;

  return (
    <NextHead>
      <title>{title}</title>
      <meta name="robots" content={robots} />
      {description && <meta name="description" content={description} />}
      <meta property="og:title" content={ogTitle} />
      <meta name="twitter:title" content={ogTitle} />
      {ogDescription && (
        <>
          <meta property="og:description" content={ogDescription} />
          <meta name="twitter:description" content={ogDescription} />
        </>
      )}
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:image" content={ogImage} />
      {hreflangLinks?.map(link => (
        <link key={`hreflang-${link.hreflang}`} rel="alternate" hrefLang={link.hreflang} href={link.href} />
      ))}
      {paginationLinks?.prev && <link key="prev" rel="prev" href={paginationLinks.prev} />}
      {paginationLinks?.next && <link key="next" rel="next" href={paginationLinks.next} />}
    </NextHead>
  );
};
